/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import type { UiRadius, UiSize } from './types.js'
import { pickStyleProps } from './types.js'

export interface ComboboxOption {
  value: string
  label: string
  disabled?: boolean
}

export interface ComboboxProps {
  /** Option list — accepts strings or { value, label, disabled } objects. */
  data?: (string | ComboboxOption)[]
  /** Currently selected value. */
  value?: string
  /** Placeholder shown when nothing is selected. */
  placeholder?: string
  label?: string
  description?: string
  error?: string | boolean
  disabled?: boolean
  /** Whether the selected value can be cleared. */
  clearable?: boolean
  /** Message shown when no options match the search query. */
  nothingFound?: string
  id?: string
  name?: string
  required?: boolean
  withAsterisk?: boolean
  size?: UiSize
  radius?: UiRadius
  class?: string
  [key: string]: unknown
}

const CHEVRON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="0.75em" height="0.75em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6l4 4 4-4"/></svg>'

function Combobox({
  data = [],
  value: initialValue = '',
  placeholder = 'Search or select…',
  label,
  description,
  error,
  disabled = false,
  clearable = false,
  nothingFound = 'Nothing found',
  id,
  name,
  required = false,
  withAsterisk = false,
  size,
  radius: r,
  class: className = '',
  ...rest
}: ComboboxProps) {
  const [open, setOpen] = createSignal(false)
  const [value, setValue] = createSignal(initialValue)
  const [search, setSearch] = createSignal('')
  const [activeValue, setActiveValue] = createSignal<string | null>(null)

  const options: ComboboxOption[] = data.map((d) =>
    typeof d === 'string' ? { value: d, label: d } : d,
  )

  const selected = () => options.find((o) => o.value === value())
  const filtered = () => {
    const q = search().toLowerCase()
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options
  }
  const navigable = () => filtered().filter((o) => !o.disabled)

  const hasAsterisk = required || withAsterisk
  const hasError = !!error

  const cssVars: Record<string, string> = {}
  if (size !== undefined) {
    cssVars['--dv-input-fz'] = `var(--dv-input-fz-${size})`
    cssVars['--dv-input-size'] = `var(--dv-input-size-${size})`
  }
  if (r !== undefined) {
    const radii: Record<string, string> = {
      xs: 'var(--dv-radius-xs)',
      sm: 'var(--dv-radius-sm)',
      md: 'var(--dv-radius-md)',
      lg: 'var(--dv-radius-lg)',
      xl: 'var(--dv-radius-xl)',
    }
    cssVars['--dv-input-radius'] = radii[r] ?? r
  }
  const { ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const style = Object.keys(cssVars).length
    ? Object.entries(cssVars)
        .map(([k, v]) => `${k}:${v}`)
        .join(';')
    : undefined

  function openDropdown() {
    if (disabled) return
    setActiveValue(null)
    setOpen(true)
  }

  function closeDropdown() {
    setOpen(false)
    setSearch('')
    setActiveValue(null)
  }

  function selectOption(opt: ComboboxOption) {
    if (opt.disabled) return
    setValue(opt.value)
    closeDropdown()
  }

  function clearValue(e: Event) {
    e.stopPropagation()
    setValue('')
    setSearch('')
  }

  function handleInput(e: Event) {
    const v = (e.target as HTMLInputElement).value
    setSearch(v)
    setActiveValue(null)
    if (!open()) setOpen(true)
  }

  function handleFocus() {
    if (!open()) openDropdown()
  }

  function handleBlur() {
    setTimeout(() => closeDropdown(), 150)
  }

  function handleKeyDown(e: KeyboardEvent) {
    const nav = navigable()
    const currentIdx = nav.findIndex((o) => o.value === activeValue())

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open()) {
        setOpen(true)
        return
      }
      const next = nav[currentIdx + 1]
      if (next) setActiveValue(next.value)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = nav[currentIdx - 1]
      if (prev) setActiveValue(prev.value)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const opt = nav.find((o) => o.value === activeValue())
      if (opt) selectOption(opt)
    } else if (e.key === 'Escape') {
      closeDropdown()
    }
  }

  const inputValue = () => (open() ? search() : (selected()?.label ?? ''))

  return (
    <div
      class={`dv-input-wrapper dv-combobox${className ? ` ${className}` : ''}`}
      style={style}
      data-disabled={disabled || undefined}
      data-error={hasError || undefined}
      data-opened={() => open() || undefined}
      {...ownRest}
    >
      {label && (
        <label class='dv-input-label' for={id} data-required={hasAsterisk || undefined}>
          {label}
          {hasAsterisk && (
            <span class='dv-input-asterisk' aria-hidden='true'>
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {description && <p class='dv-input-description'>{description}</p>}
      <div class='dv-combobox-wrapper'>
        <input
          class='dv-combobox-input'
          id={id}
          role='combobox'
          aria-expanded={() => open()}
          aria-autocomplete='list'
          aria-haspopup='listbox'
          value={() => inputValue()}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
        {() =>
          clearable && value() ? (
            <button
              type='button'
              class='dv-select-clear dv-combobox-clear'
              onClick={clearValue}
              aria-label='Clear'
              tabIndex={-1}
            >
              ×
            </button>
          ) : undefined
        }
        <span class='dv-select-chevron' aria-hidden='true' dangerouslySetInnerHTML={{ __html: CHEVRON }} />
      </div>
      <div class='dv-select-dropdown' role='listbox' data-opened={() => open() || undefined}>
        {() => {
          const opts = filtered()
          if (opts.length === 0) return <div class='dv-select-empty'>{nothingFound}</div>
          return opts.map((opt) => (
            <div
              class='dv-select-option'
              role='option'
              aria-selected={opt.value === value()}
              data-selected={opt.value === value() || undefined}
              data-disabled={opt.disabled || undefined}
              data-active={opt.value === activeValue() || undefined}
              onMouseDown={(e: Event) => e.preventDefault()}
              onClick={() => selectOption(opt)}
            >
              {opt.label}
            </div>
          ))
        }}
      </div>
      {name && <input type='hidden' name={name} value={() => value()} />}
      {hasError && error !== true && <p class='dv-input-error'>{error as string}</p>}
    </div>
  )
}

export default reactive(Combobox)
